import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Project, ProjectTemplate, ProjectType, ProjectStatus } from 'src/types/project';

interface ProjectState {
    currentProject: Project | null;
    projects: Project[];
    templates: ProjectTemplate[];
    recentProjects: string[];
    searchQuery: string;
    filterStatus: ProjectStatus | 'all';
    filterType: ProjectType | 'all';
    loading: boolean;
    error: string | null;
}

const initialState: ProjectState = {
    currentProject: null,
    projects: [],
    templates: [
        {
            id: 'drone-delivery-template',
            name: 'Drone Delivery System',
            description: 'Autonomous drone for package delivery with GPS navigation',
            type: 'drone_delivery',
            config: {
                ros2: {
                    distro: 'humble',
                    workspace: 'delivery_ws',
                    packages: ['px4_msgs', 'geometry_msgs', 'nav_msgs']
                },
                gazebo: {
                    version: '11',
                    world: 'delivery_world.sdf',
                    models: ['quadrotor', 'delivery_pad']
                }
            },
            metadata: {
                objectives: [
                    'Autonomous takeoff and landing',
                    'GPS waypoint navigation',
                    'Package pickup and delivery',
                    'Obstacle avoidance'
                ],
                milestones: [
                    {
                        id: '1',
                        name: 'Flight Control',
                        description: 'Implement basic flight control and stabilization',
                        completed: false,
                        tasks: []
                    },
                    {
                        id: '2',
                        name: 'Navigation System',
                        description: 'GPS-based waypoint navigation',
                        completed: false,
                        tasks: []
                    }
                ]
            }
        },
        {
            id: 'underwater-inspection-template',
            name: 'Underwater Pipeline Inspector',
            description: 'AUV for autonomous underwater pipeline inspection',
            type: 'underwater_inspection',
            config: {
                ros2: {
                    distro: 'humble',
                    workspace: 'auv_ws',
                    packages: ['underwater_msgs', 'sensor_msgs', 'cv_bridge']
                },
                gazebo: {
                    version: '11',
                    world: 'underwater_world.sdf',
                    models: ['auv', 'pipeline']
                }
            },
            metadata: {
                objectives: [
                    'Autonomous underwater navigation',
                    'Pipeline following',
                    'Defect detection using computer vision',
                    'Data collection and reporting'
                ]
            }
        }
    ],
    recentProjects: [],
    searchQuery: '',
    filterStatus: 'all',
    filterType: 'all',
    loading: false,
    error: null
};

const projectSlice = createSlice({
    name: 'project',
    initialState,
    reducers: {
        setCurrentProject: (state, action: PayloadAction<Project | null>) => {
            state.currentProject = action.payload;
            if (action.payload && !state.recentProjects.includes(action.payload.id)) {
                state.recentProjects.unshift(action.payload.id);
                state.recentProjects = state.recentProjects.slice(0, 10); // Keep only 10 recent projects
            }
        },

        createProject: (state, action: PayloadAction<Omit<Project, 'id' | 'created' | 'modified'>>) => {
            const newProject: Project = {
                ...action.payload,
                id: crypto.randomUUID(),
                created: new Date(),
                modified: new Date()
            };
            state.projects.push(newProject);
            state.currentProject = newProject;
            state.recentProjects.unshift(newProject.id);
        },

        updateProject: (state, action: PayloadAction<Partial<Project> & { id: string }>) => {
            const index = state.projects.findIndex(p => p.id === action.payload.id);
            if (index !== -1) {
                state.projects[index] = {
                    ...state.projects[index],
                    ...action.payload,
                    modified: new Date()
                };
                if (state.currentProject?.id === action.payload.id) {
                    state.currentProject = state.projects[index];
                }
            }
        },

        deleteProject: (state, action: PayloadAction<string>) => {
            state.projects = state.projects.filter(p => p.id !== action.payload);
            state.recentProjects = state.recentProjects.filter(id => id !== action.payload);
            if (state.currentProject?.id === action.payload) {
                state.currentProject = null;
            }
        },

        duplicateProject: (state, action: PayloadAction<string>) => {
            const original = state.projects.find(p => p.id === action.payload);
            if (original) {
                const duplicate: Project = {
                    ...original,
                    id: crypto.randomUUID(),
                    name: `${original.name} (Copy)`,
                    created: new Date(),
                    modified: new Date(),
                    status: 'draft'
                };
                state.projects.push(duplicate);
            }
        },

        createProjectFromTemplate: (state, action: PayloadAction<{ templateId: string; projectName: string; author: string }>) => {
            const template = state.templates.find(t => t.id === action.payload.templateId);
            if (template) {
                const newProject: Project = {
                    id: crypto.randomUUID(),
                    name: action.payload.projectName,
                    description: template.description,
                    type: template.type,
                    version: '1.0.0',
                    created: new Date(),
                    modified: new Date(),
                    author: action.payload.author,
                    tags: [],
                    status: 'draft',
                    config: {
                        ros2: template.config.ros2 || { distro: 'humble', workspace: '', packages: [] },
                        gazebo: template.config.gazebo || { version: '11', world: '', models: [] },
                        hardware: template.config.hardware || { robots: [], sensors: [] },
                        simulation: template.config.simulation || { physics: true, realTime: true, gui: true }
                    },
                    metadata: {
                        objectives: template.metadata?.objectives || [],
                        milestones: template.metadata?.milestones || [],
                        requirements: template.metadata?.requirements || [],
                        documentation: template.metadata?.documentation || ''
                    }
                };
                state.projects.push(newProject);
                state.currentProject = newProject;
                state.recentProjects.unshift(newProject.id);
            }
        },

        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
        },

        setFilterStatus: (state, action: PayloadAction<ProjectStatus | 'all'>) => {
            state.filterStatus = action.payload;
        },

        setFilterType: (state, action: PayloadAction<ProjectType | 'all'>) => {
            state.filterType = action.payload;
        },

        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },

        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },

        importProjects: (state, action: PayloadAction<Project[]>) => {
            state.projects = [...state.projects, ...action.payload];
        },

        clearRecentProjects: (state) => {
            state.recentProjects = [];
        }
    }
});

export const {
    setCurrentProject,
    createProject,
    updateProject,
    deleteProject,
    duplicateProject,
    createProjectFromTemplate,
    setSearchQuery,
    setFilterStatus,
    setFilterType,
    setLoading,
    setError,
    importProjects,
    clearRecentProjects
} = projectSlice.actions;

export default projectSlice.reducer;